import { DashboardLayout } from '@/components/app/DashboardLayout'
import { StatCard } from '@/components/app/StatCard'
import { ChildAvatar } from '@/components/app/ChildAvatar'
import { RiskLevelBadge } from '@/components/app/RiskLevelBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { AssessmentStatus, Child, Assessment, Prediction } from '@/types/dashboard.types'
import { ChildAPI, AssessmentAPI, AuthAPI } from '@/services/api'
import { Link } from '@tanstack/react-router'
import { Eye, Loader2, Plus, Baby, CircleCheck, Hourglass, Hand, ClipboardList } from 'lucide-react'
import { useState, useEffect } from 'react'

export const ParentDashboard = () => {
  const [user, setUser] = useState<any>(null)
  const [children, setChildren] = useState<Child[]>([])
  const [assessments, setAssessments] = useState<Assessment[]>([])
  const [dashboardStats, setDashboardStats] = useState<any>(null)
  const [predictions, setPredictions] = useState<Prediction[]>([])

  const [isLoading, setIsLoading] = useState(true)
  const [showEnglish, setShowEnglish] = useState(false)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [userData, childrenData, assessmentsPayload] = await Promise.all([
          AuthAPI.me(),
          ChildAPI.getChildren(),
          AssessmentAPI.getAssessments()
        ]);

        // Ensure user obj fits the shape
        setUser({ ...userData.user })
        setChildren(childrenData)

        const assessmentsArr = assessmentsPayload.assessments || assessmentsPayload;
        setAssessments(assessmentsArr)
        setDashboardStats(assessmentsPayload.dashboardStats || null);

        // Extract predictions from assessments that have them embedded
        const embeddedPredictions = assessmentsArr
          .map((a: any) => a.prediction ? { ...a.prediction, assessmentId: a.id } : null)
          .filter(Boolean)
        setPredictions(embeddedPredictions)
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchDashboardData()
  }, [])

  if (isLoading || !user) {
    return (
      <DashboardLayout user={user || { firstName: 'User', lastName: '', email: '', role: 'PARENT' }}>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    )
  }

  // Use dashboard stats provided by the backend API
  const totalChildren = children.length
  const totalAssessments = dashboardStats?.totalAnalyzed || 0
  const inProgressAssessments = dashboardStats?.inProgress || 0
  const lastAssessment = dashboardStats?.lastAssessment || null

  // Date formatting helper
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6 animate-in fade-in duration-500">
        {/* Welcome Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="animate-in slide-in-from-left duration-700 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent sm:text-3xl">
                Welcome back, {user.firstName}!
              </h1>
              <Hand className="h-7 w-7 shrink-0 text-teal-600" aria-hidden />
            </div>
            <p className="text-muted-foreground mt-1">
              Track your children's speech and language development progress
            </p>
            {showEnglish && (
              <p className="text-sm text-blue-600 mt-1 italic">
                English: Track your children's speech and language development progress
              </p>
            )}
          </div>
          <div className="flex w-full animate-in slide-in-from-right flex-col gap-2 duration-700 sm:w-auto sm:flex-row sm:flex-wrap">
            <Button
              variant={showEnglish ? "default" : "outline"}
              onClick={() => setShowEnglish(!showEnglish)}
              className="w-full gap-2 sm:w-auto"
            >
              <Eye className="h-4 w-4" />
              {showEnglish ? 'Hide English' : 'Show English'}
            </Button>
            <Link to="/dashboard/assessment" className="w-full sm:w-auto">
              <Button size="lg" variant="default" className="w-full gap-2 sm:w-auto">
                <Plus className="h-4 w-4 shrink-0" aria-hidden />
                <span>Start Assessment</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-3 animate-in slide-in-from-top duration-700">
          <StatCard title="My Children" value={totalChildren} icon={Baby} />
          <StatCard
            title="Completed Assessments"
            value={totalAssessments}
            icon={CircleCheck}
            description={
              lastAssessment
                ? `Last: ${formatDate(lastAssessment.completedAt!)}`
                : 'No assessments yet'
            }
          />
          <StatCard
            title="In Progress"
            value={inProgressAssessments}
            icon={Hourglass}
            description={inProgressAssessments > 0 ? 'Resume assessment' : 'No pending assessments'}
          />
        </div>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 h-auto">
            <TabsTrigger value="overview" className="py-2">Overview</TabsTrigger>
            <TabsTrigger value="children" className="py-2">My Children</TabsTrigger>
            <TabsTrigger value="history" className="py-2">History</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card className="border-transparent bg-muted/30 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Start or continue your child assessment workflow</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2">
                <Link to="/dashboard/assessment" className="w-full">
                  <Button size="lg" variant="default" className="w-full">Start New Assessment</Button>
                </Link>
                <Link to="/dashboard/children" className="w-full">
                  <Button variant="outline" size="lg" className="w-full">Manage Children</Button>
                </Link>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="children">
            <Card className="border-transparent bg-gradient-to-br from-teal-50 to-teal-100/50 backdrop-blur-sm dark:from-card dark:to-muted/50 dark:border dark:border-border">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle>My Children</CardTitle>
                  <CardDescription>Manage your children's profiles</CardDescription>
                </div>
                <Link to="/dashboard/children" className="w-full sm:w-auto">
                  <Button variant="outline" size="sm" className="w-full sm:w-auto gap-2">
                    <Plus className="h-4 w-4 shrink-0" aria-hidden />
                    <span>Add Child</span>
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {children.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No child profile yet. Add your first child to start assessments.</p>
                ) : (
                  <div className="grid gap-4 lg:grid-cols-2">
                    {children.map((child) => {
                      const childAssessments = assessments.filter((a) => a.childId === child.id)
                      const lastChildAssessment = childAssessments
                        .filter((a) => a.completedAt)
                        .sort(
                          (a, b) =>
                            new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime()
                        )[0]
                      const lastPrediction = lastChildAssessment
                        ? predictions.find((p) => p.assessmentId === lastChildAssessment.id)
                        : null

                      return (
                        <Card key={child.id} className="border-transparent bg-card hover:shadow-md transition-all dark:border dark:border-border">
                          <CardContent className="pt-6">
                            <div className="flex items-start gap-4">
                              <ChildAvatar gender={child.gender} size="lg" />
                              <div className="flex-1 space-y-2">
                                <div>
                                  <h3 className="text-lg font-semibold">
                                    {child.firstName} {child.lastName}
                                  </h3>
                                  <p className="text-sm text-muted-foreground">
                                    {child.ageInMonths} months old • Born {formatDate(child.dateOfBirth)}
                                  </p>
                                </div>

                                {lastPrediction && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Last Result:</span>
                                    <RiskLevelBadge riskLevel={lastPrediction.riskLevel} size="sm" />
                                  </div>
                                )}

                                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                                  <Link to={`/dashboard/assessment/${child.id}`} className="w-full sm:w-auto">
                                    <Button size="sm" variant="default" className="w-full sm:w-auto gap-1">
                                      <ClipboardList className="h-4 w-4 shrink-0" aria-hidden />
                                      <span>New Assessment</span>
                                    </Button>
                                  </Link>
                                  <Link to={`/dashboard/children`} className="w-full sm:w-auto">
                                    <Button size="sm" variant="outline" className="w-full sm:w-auto gap-1">
                                      <Eye className="h-4 w-4 shrink-0" aria-hidden />
                                      <span>View Profile</span>
                                    </Button>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="border-transparent bg-muted/30 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Assessment History</CardTitle>
                <CardDescription>View your recent assessments and results</CardDescription>
              </CardHeader>
              <CardContent>
                {assessments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No assessment history yet.</p>
                ) : (
                  <ScrollArea className="h-[420px] pr-2">
                    <div className="space-y-4">
                      {assessments
                        .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
                        .map((assessment) => {
                          const child = children.find((c) => c.id === assessment.childId)
                          const prediction = predictions.find((p) => p.assessmentId === assessment.id)

                          return (
                            <div
                              key={assessment.id}
                              className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 border border-border bg-card rounded-lg hover:shadow-sm transition-all"
                            >
                              <div className="flex items-start gap-4">
                                {child && <ChildAvatar gender={child.gender} size="sm" />}
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-semibold">
                                      {child?.firstName} {child?.lastName}
                                    </h4>
                                    {assessment.status === AssessmentStatus.IN_PROGRESS && (
                                      <Badge variant="secondary">In Progress</Badge>
                                    )}
                                    {assessment.status === AssessmentStatus.COMPLETED && (
                                      <Badge variant="outline">Processing</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    Started: {formatDateTime(assessment.startedAt)}
                                  </p>
                                  {assessment.completedAt && (
                                    <p className="text-sm text-muted-foreground">
                                      Completed: {formatDateTime(assessment.completedAt)}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 flex-wrap">
                                {prediction && <RiskLevelBadge riskLevel={prediction.riskLevel} />}
                                {assessment.status === AssessmentStatus.IN_PROGRESS ? (
                                  <Link to={`/dashboard/assessment/${assessment.id}/continue`}>
                                    <Button size="sm" variant="default">
                                      Continue
                                    </Button>
                                  </Link>
                                ) : (
                                  <Link to={`/dashboard/reports/${assessment.id}`}>
                                    <Button size="sm" variant="outline">
                                      View Report
                                    </Button>
                                  </Link>
                                )}
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  )
}
